import { DOCUMENT, inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { API_BASE_PUBLIC_URL } from '../utils/di.tokens';
import {
  NotificationHubEvent,
  NotificationHubEvents,
} from './notification-hub-events';
import {
  BehaviorSubject,
  Observable,
  Subject,
  filter,
  firstValueFrom,
  map,
  race,
  timer,
} from 'rxjs';

// Ulanish holatlarini aniq belgilash
export enum SignalRConnectionState {
  Disconnected = 'Disconnected',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
}

/**
 * Socket loglari uchun ranglar va uslublar (Brauzer konsolida ko'rkam ko'rinishi uchun)
 */
const SOCKET_LOG_STYLES = {
  inbound:
    'background: #059669; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-weight: 700; font-size: 11px;',
  outbound:
    'background: #2563eb; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-weight: 700; font-size: 11px;',
  response:
    'background: #4f46e5; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-weight: 700; font-size: 11px;',
  error:
    'background: #dc2626; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-weight: 700; font-size: 11px;',
  titleIn: 'color: #059669; font-weight: 700; padding-left: 4px;',
  titleOut: 'color: #2563eb; font-weight: 700; padding-left: 4px;',
  titleRes: 'color: #4f46e5; font-weight: 700; padding-left: 4px;',
  titleErr: 'color: #dc2626; font-weight: 700; padding-left: 4px;',
  time: 'color: #9ca3af; font-size: 10px; font-weight: normal; padding-left: 4px;',
};

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private document = inject(DOCUMENT);
  private apiBasePublicUrl = inject(API_BASE_PUBLIC_URL, { optional: true });

  /**
   * Socket loglarini yoqish yoki o'chirish bayrog'i
   */
  public isLoggingEnabled = true;

  constructor() {
    if (typeof window !== 'undefined') {
      (window as any).__toggleSocketLogs = (enable?: boolean) => {
        this.isLoggingEnabled =
          typeof enable === 'boolean' ? enable : !this.isLoggingEnabled;
        console.log(
          `%c[Socket] Logging: ${this.isLoggingEnabled ? 'YOQILDI (ON)' : "O'CHIRILDI (OFF)"
          }`,
          this.isLoggingEnabled
            ? 'color: #10b981; font-weight: bold;'
            : 'color: #ef4444; font-weight: bold;',
        );
        return this.isLoggingEnabled;
      };
      (window as any).__signalRService = this;
    }
  }

  /**
   * Joriy vaqtni (HH:mm:ss.SSS) formatda qaytarish
   */
  private getFormattedTime(): string {
    const d = new Date();
    const pad = (n: number, s = 2) => n.toString().padStart(s, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
      d.getSeconds(),
    )}.${pad(d.getMilliseconds(), 3)}`;
  }

  /**
   * Kiruvchi socket hodisasini konsolga chiqarish (Server -> Client)
   */
  private logIncoming(eventName: string, payload: any): void {
    if (!this.isLoggingEnabled) return;
    const time = this.getFormattedTime();
    console.log(
      `%c[Socket ⬇ KIRISH] %c${eventName} %c@ ${time}`,
      SOCKET_LOG_STYLES.inbound,
      SOCKET_LOG_STYLES.titleIn,
      SOCKET_LOG_STYLES.time,
      payload !== undefined ? payload : "(bo'sh)",
    );
  }

  /**
   * Chiquvchi socket hodisasini konsolga chiqarish (Client -> Server)
   */
  private logOutgoing(methodName: string, args: any[]): void {
    if (!this.isLoggingEnabled) return;
    const time = this.getFormattedTime();
    const payload =
      args && args.length === 1
        ? args[0]
        : args?.length
          ? args
          : "(parametr yo'q)";
    console.log(
      `%c[Socket ⬆ CHIQISH] %c${methodName} %c@ ${time}`,
      SOCKET_LOG_STYLES.outbound,
      SOCKET_LOG_STYLES.titleOut,
      SOCKET_LOG_STYLES.time,
      payload,
    );
  }

  /**
   * Chiquvchi so'rov muvaffaqiyatli serverdan javob olganda
   */
  private logOutgoingSuccess(
    methodName: string,
    result: any,
    durationMs: number,
  ): void {
    if (!this.isLoggingEnabled) return;
    const time = this.getFormattedTime();
    console.log(
      `%c[Socket ⬆ JAVOB (${durationMs}ms)] %c${methodName} %c@ ${time}`,
      SOCKET_LOG_STYLES.response,
      SOCKET_LOG_STYLES.titleRes,
      SOCKET_LOG_STYLES.time,
      result !== undefined ? result : '(muvaffaqiyatli / void)',
    );
  }

  /**
   * Chiquvchi so'rovda xatolik yuz berganda
   */
  private logOutgoingError(
    methodName: string,
    error: any,
    durationMs: number,
  ): void {
    if (!this.isLoggingEnabled) return;
    const time = this.getFormattedTime();
    console.error(
      `%c[Socket ⬆ XATOLIK (${durationMs}ms)] %c${methodName} %c@ ${time}`,
      SOCKET_LOG_STYLES.error,
      SOCKET_LOG_STYLES.titleErr,
      SOCKET_LOG_STYLES.time,
      error,
    );
  }

  /**
   * HubConnection ga kiruvchi va chiquvchi hodisalarni kuzatuvchi wrapperlarni ulash
   */
  private wrapConnectionLogging(connection: signalR.HubConnection): void {
    // 1. Chiquvchi xabarlar: invoke
    const originalInvoke = connection.invoke.bind(connection);
    connection.invoke = async <T = any>(
      methodName: string,
      ...args: any[]
    ): Promise<T> => {
      const startTime = performance.now();
      this.logOutgoing(methodName, args);
      try {
        const result = await originalInvoke(methodName, ...args);
        const duration = Math.round(performance.now() - startTime);
        this.logOutgoingSuccess(methodName, result, duration);
        return result;
      } catch (error) {
        const duration = Math.round(performance.now() - startTime);
        this.logOutgoingError(methodName, error, duration);
        throw error;
      }
    };

    // 2. Chiquvchi xabarlar: send
    const originalSend = connection.send.bind(connection);
    connection.send = async (
      methodName: string,
      ...args: any[]
    ): Promise<void> => {
      const startTime = performance.now();
      this.logOutgoing(methodName, args);
      try {
        const result = await originalSend(methodName, ...args);
        const duration = Math.round(performance.now() - startTime);
        this.logOutgoingSuccess(methodName, result, duration);
        return result;
      } catch (error) {
        const duration = Math.round(performance.now() - startTime);
        this.logOutgoingError(methodName, error, duration);
        throw error;
      }
    };

    // 3. Kiruvchi xabarlar: _invokeClientMethod
    const connectionAny = connection as any;
    if (typeof connectionAny._invokeClientMethod === 'function') {
      const originalInvokeClientMethod =
        connectionAny._invokeClientMethod.bind(connection);
      connectionAny._invokeClientMethod = async (invocationMessage: any) => {
        try {
          const target = invocationMessage?.target || 'UnknownEvent';
          const args = invocationMessage?.arguments || [];
          const payload =
            args.length === 1 ? args[0] : args.length === 0 ? undefined : args;
          this.logIncoming(target, payload);
        } catch (err) {
          console.error(
            '[Socket Logger] Kiruvchi hodisani loglashda xatolik:',
            err,
          );
        }
        return originalInvokeClientMethod(invocationMessage);
      };
    }
  }

  /**
   * Hub API serverining ildizida (`{origin}/notification`). Production'da sahifa
   * va API bir domenda; lokal dev'da sahifa `localhost` bo'lgani uchun manzil
   * API URL'idan olinadi (backend localhost uchun CORS'ni ochgan).
   */
  private get hubUrl(): string {
    try {
      if (this.apiBasePublicUrl) {
        return new URL(this.apiBasePublicUrl).origin + '/notification';
      }
    } catch {
      // noto'g'ri URL — sahifa domeniga qaytamiz
    }
    return this.document.location.origin + '/notification';
  }

  // Ulanish holatini butun tizim bo'ylab kuzatish uchun
  private connectionStateSubject = new BehaviorSubject<SignalRConnectionState>(
    SignalRConnectionState.Disconnected,
  );
  public connectionState$: Observable<SignalRConnectionState> =
    this.connectionStateSubject.asObservable();

  // Global xabarlar uchun
  public messageReceived$ = new Subject<any>();

  /**
   * Tizimga kirgandan so'ng chaqiriladi
   */
  public startConnection(): void {
    if (
      this.hubConnection?.state === signalR.HubConnectionState.Connected ||
      this.hubConnection?.state === signalR.HubConnectionState.Connecting
    ) {
      return;
    }

    this.connectionStateSubject.next(SignalRConnectionState.Connecting);
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => localStorage.getItem('accessToken') || '',
        // Avtorizatsiya Bearer token orqali; cookie kerak emas. `true` bo'lsa
        // boshqa domenga (lokal dev → tolimpadm) negotiate CORS'da yiqiladi.
        withCredentials: false,
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .configureLogging(signalR.LogLevel.Warning)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // 30 soniya ham qo'shildi
      .build();

    // Kiruvchi va chiquvchi xabarlarni loglash wrapperlarini o'rnatish
    this.wrapConnectionLogging(this.hubConnection);

    this.registerLifecycleEvents();
    this.registerServerEvents();

    this.hubConnection
      .start()
      .then(() => {
        this.connectionStateSubject.next(SignalRConnectionState.Connected);
      })
      .catch(() => {
        this.connectionStateSubject.next(SignalRConnectionState.Disconnected);
      });
  }

  /**
   * Tizimdan chiqishda ulanishni to'liq uzish
   */
  public stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection
        .stop()
        .then(() => {
          this.connectionStateSubject.next(SignalRConnectionState.Disconnected);
        })
        .catch(() => undefined);
      this.hubConnection = null;
    }
  }

  /**
   * Ulanish o'rnatilguncha kutadi (kerak bo'lsa ulanishni o'zi boshlaydi).
   * Backend progress xabarlarini saqlamaydi — shuning uchun uzoq jarayonni
   * boshlovchi so'rovdan OLDIN chaqirilishi kerak. Ulanib bo'lmasa `false`
   * qaytaradi, jarayon esa progress'siz davom etaveradi.
   */
  public async ensureConnected(timeoutMs = 5000): Promise<boolean> {
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return true;
    }
    const isStopped =
      !this.hubConnection ||
      this.hubConnection.state === signalR.HubConnectionState.Disconnected;
    if (isStopped && localStorage.getItem('accessToken')) {
      this.startConnection();
    }
    if (!this.hubConnection) {
      return false;
    }

    return firstValueFrom(
      race(
        this.connectionState$.pipe(
          filter((state) => state === SignalRConnectionState.Connected),
          map(() => true),
        ),
        timer(timeoutMs).pipe(map(() => false)),
      ),
    );
  }

  /**
   * Server event'ini Observable sifatida tinglash — unsubscribe qilinganda
   * handler ulanishdan olib tashlanadi.
   */
  public on$<T>(methodName: NotificationHubEvent): Observable<T> {
    return new Observable<T>((subscriber) => {
      const connection = this.hubConnection;
      if (!connection) {
        return;
      }
      const handler = (payload: T) => subscriber.next(payload);
      connection.on(methodName, handler);
      return () => connection.off(methodName, handler);
    });
  }

  // 3-YANGI FEATURE: Backendga ma'lumot yuborish (Invoke)
  public async sendToServer(methodName: string, ...args: any[]): Promise<any> {
    if (this.hubConnection?.state !== signalR.HubConnectionState.Connected) {
      return Promise.reject('No connection');
    }
    return this.hubConnection.invoke(methodName, ...args);
  }

  // 4-YANGI FEATURE: Istalgan metodni dinamik tinglash uchun qulay funksiya
  public onMessage(
    methodName: NotificationHubEvent,
    callback: (...args: any[]) => void,
  ): void {
    this.hubConnection?.on(methodName, callback);
  }

  public offMessage(
    methodName: NotificationHubEvent,
    callback: (...args: any[]) => void,
  ): void {
    this.hubConnection?.off(methodName, callback);
  }

  /**
   * SignalR ning ichki holat o'zgarishlarini tinglash
   */
  private registerLifecycleEvents(): void {
    if (!this.hubConnection) return;

    // Internet uzilib qolsa va qayta ulanishga urinsa
    this.hubConnection.onreconnecting((error) => {
      this.connectionStateSubject.next(SignalRConnectionState.Reconnecting);
    });

    // Qayta ulanish muvaffaqiyatli bo'lsa
    this.hubConnection.onreconnected((connectionId) => {
      this.connectionStateSubject.next(SignalRConnectionState.Connected);
    });

    // Ulanish butunlay uzilib qolsa
    this.hubConnection.onclose((error?: Error) => {
      this.connectionStateSubject.next(SignalRConnectionState.Disconnected);
    });
  }

  /**
   * Serverdan keladigan global xabarlar
   */
  private registerServerEvents(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on(NotificationHubEvents.ReceiveNotification, (data) => {
      this.messageReceived$.next(data);
    });

    this.hubConnection.on(
      NotificationHubEvents.ForceLogout,
      () => {
        this.stopConnection();
      },
    );
  }
}
